import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const LOGO = '/dailyreps3.png';

export default function CommunicationPreferences() {
  const { user, apiFetch, logout } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [emailTeamAlerts, setEmailTeamAlerts] = useState(true);
  const [emailTipsUpdates, setEmailTipsUpdates] = useState(true);
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    apiFetch('/api/email-preferences')
      .then(data => {
        setEmailTeamAlerts(data.email_team_alerts !== false);
        setEmailTipsUpdates(data.email_tips_updates !== false);
      })
      .catch(() => setError('Failed to load preferences.'))
      .finally(() => setLoading(false));
  }, [apiFetch]);

  const savePreferences = useCallback(async (teamAlerts, tipsUpdates) => {
    setSaving(true);
    setError('');
    setSuccessMessage('');
    try {
      await apiFetch('/api/email-preferences', {
        method: 'PUT',
        body: JSON.stringify({
          email_team_alerts: teamAlerts,
          email_tips_updates: tipsUpdates,
        }),
      });
      setSuccessMessage('Preferences saved.');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to save preferences.');
    } finally {
      setSaving(false);
    }
  }, [apiFetch]);

  const handleToggleTeamAlerts = () => {
    const next = !emailTeamAlerts;
    setEmailTeamAlerts(next);
    savePreferences(next, emailTipsUpdates);
  };

  const handleToggleTipsUpdates = () => {
    const next = !emailTipsUpdates;
    setEmailTipsUpdates(next);
    savePreferences(emailTeamAlerts, next);
  };

  const backPath = user?.role === 'club_admin' ? '/club'
    : user?.role === 'parent' ? '/parent'
    : user?.role === 'super_admin' ? '/super'
    : '/admin';

  if (loading) {
    return (
      <div className="admin-layout">
        <header className="header">
          <div className="header-left">
            <img src={LOGO} alt="Train Daily" className="header-logo" />
            <div className="header-title">Communication Preferences</div>
          </div>
        </header>
        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 80 }}>
          <div className="loading"><div className="spinner" /></div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-layout">
      <header className="header">
        <div className="header-left">
          <img src={LOGO} alt="Train Daily" className="header-logo" />
          <div className="header-title">Communication Preferences</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button className="btn btn-sm btn-outline" onClick={() => navigate(backPath)}>Back</button>
          <button className="header-logout" onClick={logout}>Log out</button>
        </div>
      </header>

      <div style={{ padding: '16px 16px 80px', maxWidth: 600, margin: '0 auto' }}>

        {error && (
          <div style={{ color: 'var(--danger)', fontSize: '0.85rem', marginBottom: 12, textAlign: 'center' }}>
            {error}
          </div>
        )}

        {successMessage && (
          <div style={{ color: 'var(--success)', fontSize: '0.85rem', marginBottom: 12, textAlign: 'center' }}>
            {successMessage}
          </div>
        )}

        <div className="card" style={{ marginBottom: 16, padding: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: '0.95rem', color: '#fff' }}>Team alerts and reports</div>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 4 }}>
                Weekly summaries, alerts, challenge updates, and the drill digest.
              </div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={emailTeamAlerts}
              disabled={saving}
              onClick={handleToggleTeamAlerts}
              style={{
                position: 'relative',
                width: 52,
                height: 28,
                borderRadius: 14,
                border: 'none',
                background: emailTeamAlerts ? '#f77c00' : '#444',
                cursor: saving ? 'not-allowed' : 'pointer',
                transition: 'background 0.2s',
                flexShrink: 0,
                marginLeft: 16,
                padding: 0,
              }}
            >
              <span style={{
                position: 'absolute',
                top: 3,
                left: emailTeamAlerts ? 27 : 3,
                width: 22,
                height: 22,
                borderRadius: '50%',
                background: '#fff',
                transition: 'left 0.2s',
                boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
              }} />
            </button>
          </div>
        </div>

        <div className="card" style={{ marginBottom: 16, padding: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: '0.95rem', color: '#fff' }}>Tips and updates</div>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 4 }}>
                Tips, reminders, and news from Train Daily.
              </div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={emailTipsUpdates}
              disabled={saving}
              onClick={handleToggleTipsUpdates}
              style={{
                position: 'relative',
                width: 52,
                height: 28,
                borderRadius: 14,
                border: 'none',
                background: emailTipsUpdates ? '#f77c00' : '#444',
                cursor: saving ? 'not-allowed' : 'pointer',
                transition: 'background 0.2s',
                flexShrink: 0,
                marginLeft: 16,
                padding: 0,
              }}
            >
              <span style={{
                position: 'absolute',
                top: 3,
                left: emailTipsUpdates ? 27 : 3,
                width: 22,
                height: 22,
                borderRadius: '50%',
                background: '#fff',
                transition: 'left 0.2s',
                boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
              }} />
            </button>
          </div>
        </div>

        <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 20, textAlign: 'center' }}>
          Required emails (account setup and security) will always be sent.
        </p>

      </div>
    </div>
  );
}
